import { createApiClient } from "../../apiClientFactory";
import {
  Voucher,
  CreateVoucherInput,
  UpdateVoucherInput,
  VoucherFilters,
  VoucherListResponse,
  VoucherStats,
  BulkVoucherAction,
  BulkVoucherResponse,
  VoucherExportOptions,
} from "./vouchers.interfaces";
import type { IVouchersService } from "./vouchers.service";

const toApiDiscountType = (value?: string) => {
  if (!value) return undefined;
  const map: Record<string, string> = {
    percentage: 'Percentage',
    Percentage: 'Percentage',
    fixedAmount: 'FixedAmount',
    FixedAmount: 'FixedAmount',
    freeShipping: 'FreeShipping',
    FreeShipping: 'FreeShipping',
  };
  return map[value] ?? value;
};

const toApiVoucherBody = (data: CreateVoucherInput | UpdateVoucherInput) => ({
  ...data,
  ...(data.discountType ? { discountType: toApiDiscountType(data.discountType) } : {}),
});

export class VouchersApiService implements IVouchersService {
  private client = createApiClient("vouchers");

  async getVouchers(
    page: number = 1,
    pageSize: number = 12,
    filters?: VoucherFilters
  ): Promise<VoucherListResponse> {
    const params = {
      page,
      pageSize,
      ...(filters?.search && { search: filters.search }),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.isActive !== undefined && { isActive: filters.isActive }),
      ...(filters?.discountType && { discountType: filters.discountType }),
      ...(filters?.createdAfter && { createdAfter: filters.createdAfter }),
      ...(filters?.createdBefore && { createdBefore: filters.createdBefore }),
      ...(filters?.validAfter && { validAfter: filters.validAfter }),
      ...(filters?.validBefore && { validBefore: filters.validBefore }),
      ...(filters?.usageRange && { 
        usageMin: filters.usageRange.min,
        usageMax: filters.usageRange.max 
      }),
      ...(filters?.createdBy && { createdBy: filters.createdBy }),
    };

    console.log(">> Params: "+ JSON.stringify(params));

    const response = await this.client.get(`/admin/vouchers/query`, { params });

    return {
      vouchers: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
      page,
      pageSize,
      hasMore:
        page * pageSize < parseInt(response.headers["x-total-count"] || "0"),
    };
  }

  async getVoucherById(id: string): Promise<Voucher> {
    const response = await this.client.get(`/admin/vouchers/${id}`);
    return response.data;
  }

  async createVoucher(data: CreateVoucherInput): Promise<Voucher> {
    const response = await this.client.post(`/admin/vouchers`, toApiVoucherBody(data));
    return response.data;
  }

  async updateVoucher(id: string, data: UpdateVoucherInput): Promise<Voucher> {
    const response = await this.client.put(`/admin/vouchers/${id}`, toApiVoucherBody(data));
    return response.data;
  }

  async deleteVoucher(id: string): Promise<void> {
    await this.client.delete(`/admin/vouchers/${id}`);
  }

  async activateVoucher(id: string): Promise<Voucher> {
    const response = await this.client.patch(`/admin/vouchers/bulk/activate`, { ids: [id] });
    return response.data;
  }

  async deactivateVoucher(id: string): Promise<Voucher> {
    const response = await this.client.patch(`/admin/vouchers/bulk/deactivate`, { ids: [id] });
    return response.data;
  }

  async extendVoucherValidity(id: string, newValidUntil: string): Promise<Voucher> {
    const response = await this.client.patch(
      `/admin/vouchers/${id}/extend`,
      { validUntil: newValidUntil }
    );
    return response.data;
  }

  async getVoucherStats(id: string): Promise<VoucherStats> {
    const response = await this.client.get(`/admin/vouchers/${id}/stats`);
    return response.data;
  }

  // Ações em lote
  async bulkAction(action: BulkVoucherAction): Promise<BulkVoucherResponse> {
    const response = await this.client.post(`/admin/vouchers/bulk/action`, action);
    return response.data;
  }

  async bulkActivateVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    const response = await this.client.patch(`/admin/vouchers/bulk/activate`, { ids });
    return response.data;
  }

  async bulkDeactivateVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    const response = await this.client.patch(`/admin/vouchers/bulk/deactivate`, { ids });
    return response.data;
  }

  async bulkDeleteVouchers(ids: string[]): Promise<BulkVoucherResponse> {
    const response = await this.client.delete(`/admin/vouchers/bulk/delete`, {
      data: { ids },
    });
    return response.data;
  }

  async bulkExtendValidity(ids: string[], newValidUntil: string): Promise<BulkVoucherResponse> {
    const response = await this.client.patch(`/admin/vouchers/bulk/extend`, { 
      ids, 
      validUntil: newValidUntil 
    });
    return response.data;
  }

  // Exportação
  async exportVouchers(
    options: VoucherExportOptions, 
    filters?: VoucherFilters, 
    selectedIds?: string[]
  ): Promise<Blob> {
    const params = {
      format: options.format,
      columns: options.columns.join(','),
      includeSelectedOnly: options.includeSelectedOnly,
      selectedIds: selectedIds?.join(','),
      ...(filters?.search && { search: filters.search }),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.isActive !== undefined && { isActive: filters.isActive }),
      ...(filters?.discountType && { discountType: filters.discountType }),
      ...(filters?.createdAfter && { createdAfter: filters.createdAfter }),
      ...(filters?.createdBefore && { createdBefore: filters.createdBefore }),
      ...(filters?.validAfter && { validAfter: filters.validAfter }),
      ...(filters?.validBefore && { validBefore: filters.validBefore }),
      ...(filters?.usageRange && { 
        usageMin: filters.usageRange.min,
        usageMax: filters.usageRange.max 
      }),
      ...(filters?.createdBy && { createdBy: filters.createdBy }),
    };

    const response = await this.client.get(`/admin/vouchers/export`, { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }
}